/** `strike-flight/blue-max` — the strike-flight family template authored
 * from the stored Blue Max concept: one strike aircraft on a one-way run
 * over enemy territory that scrolls on a shallow pseudo-isometric diagonal
 * (the craft's nose and the ground's flow point up-right; sprites stay
 * axis-aligned, the ground art scrolls under them, aircraft fly lifted over
 * their shadows). Steer freely inside the scrolling band; the gun fires
 * forward along the scroll and kills fighters, bombers, supply trucks and
 * flak crews but passes over the hardened primary targets — only bombs
 * break those. Twelve bombs: a released bomb keeps the aircraft's momentum
 * and falls for over a second, so the impact ring slides ahead of the craft
 * and locks when it sits on a target; release EARLY. Five marked primaries
 * (fuel depot, hangar, factory, radio mast, river bridge) lie far apart
 * along three sectors — open farmland with trucks, an industrial belt with
 * paired flak, a river valley where heavy bombers cross — and the hardened
 * buildings are solid: clipping one costs hull. Five hull points: smoke at
 * three, fire and slower steering at one. Flak only engages on screen, ahead
 * of the craft and after a blinking swivel; fighters dive visibly; the first
 * eight seconds are a safe corridor. Reaching the extraction pad with 5/5
 * wins (unused bombs pay a bonus); arriving short, or being shot down, fails.
 *
 * Built ONLY from maintained modules: Wings' diver, gun, destructible and
 * contact damage, Bob's resource meter (the bomb bay's stock), the shared
 * vitals, escalation and objective — plus the strike modules (flight body,
 * bomb bay with its falling store, blast and reticle, the scroll track, the
 * turret, the target checklist and damage stages). Constants follow the
 * unmerged flight-strike profile where it matched the concept (12 bombs,
 * a 1.1 blast, ~6 u/s steering, enemy shells at ~7 u/s). */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { ScrollTrackConfig, RouteSector } from '../modules/strike.js';
import type { TemplateV1 } from './template.js';

const W = 9, H = 16;
/** The craft flies here (centre); the route is measured on this line. */
const ANCHOR = { x: 4.5, y: 11.6 };
const HEADING = 22;
const SPEED = 1.6;
const ALTITUDE = 1.1;
const PLANE = 1.1;
/** Sector lengths: farmland and the industrial belt, then the long river
 * valley climax — a five-minute run whose bombers cross from 2.5 minutes. */
const SECTORS = [120, 120, 240] as const;

const sectors: RouteSector[] = [
  // Sector one — open farmland, sparse trucks: the fuel depot teaches the lead.
  { length: SECTORS[0], marks: [
    { archetype: 'marked-target', at: 0.32, jitter: 6, across: 0.5, label: 'FUEL DEPOT', tint: 6, escorts: [] },
    { archetype: 'marked-target', at: 0.8, jitter: 8, across: -0.3, label: 'HANGAR', tint: 4, escorts: [{ archetype: 'flak-turret', along: 7, across: 2.6 }] },
  ], waves: [
    { archetype: 'supply-truck', from: 0.05, to: 0.95, everyMin: 8, everyMax: 14, acrossMin: -2.5, acrossMax: 2.5, velocity: { vx: 0.6, vy: 0.25 } },
    { archetype: 'enemy-fighter', from: 0.12, to: 1, everyMin: 20, everyMax: 32, acrossMin: -2, acrossMax: 3 },
  ] },
  // Sector two — the industrial belt: paired flak and fast fighters.
  { length: SECTORS[1], marks: [
    { archetype: 'marked-target', at: 0.3, jitter: 8, across: 0.4, label: 'FACTORY', tint: 7, escorts: [
      { archetype: 'flak-turret', along: 5, across: -2.4 }, { archetype: 'flak-turret', along: 6, across: 3.2 }] },
    { archetype: 'marked-target', at: 0.76, jitter: 8, across: -0.5, label: 'RADIO MAST', tint: 5, escorts: [{ archetype: 'flak-turret', along: 6, across: 2.6 }] },
  ], waves: [
    { archetype: 'enemy-fighter', from: 0, to: 1, everyMin: 18, everyMax: 30, acrossMin: -2, acrossMax: 3 },
    { archetype: 'supply-truck', from: 0, to: 1, everyMin: 18, everyMax: 30, acrossMin: -3, acrossMax: 3, velocity: { vx: -0.5, vy: -0.2 } },
    { archetype: 'flak-turret', from: 0.1, to: 0.95, everyMin: 26, everyMax: 40, acrossMin: -3.5, acrossMax: 3.5 },
  ] },
  // Sector three — the river valley: heavy bombers cross; the bridge is
  // defended from both banks.
  { length: SECTORS[2], marks: [
    { archetype: 'river-bridge', at: 0.62, jitter: 6, across: 0, escorts: [
      { archetype: 'flak-turret', along: 3, across: -3 }, { archetype: 'flak-turret', along: 4, across: 3 }] },
  ], waves: [
    { archetype: 'enemy-bomber', from: 0, to: 0.9, everyMin: 4, everyMax: 46, acrossMin: -1.5, acrossMax: 2.5 },
    { archetype: 'enemy-fighter', from: 0, to: 1, everyMin: 22, everyMax: 34, acrossMin: -2, acrossMax: 3 },
    { archetype: 'flak-turret', from: 0.1, to: 0.95, everyMin: 30, everyMax: 46, acrossMin: -3.5, acrossMax: 3.5 },
  ] },
];

const track: ScrollTrackConfig = { heading: HEADING, speed: SPEED, anchorTag: 'ground', anchor: ANCHOR, margin: 1,
  safeAlong: Math.ceil(SPEED * 8), markGap: 20, sectors, finale: { archetype: 'extraction-pad', across: 0 } };

/** An enemy aircraft: a diving pass (or a slow crossing) that fires aimed
 * bursts only while it is on screen. */
const aircraft = (role: string, palette: number, size: { w: number; h: number }, elevation: number, dive: { speed: number; weaveAmp: number; weaveRate: number },
  gun: { everyTicks: number; maxAlive: number }, hp: number, score: number): GameSpecV1['archetypes'][string] => ({
  role, tags: ['enemy', 'air', 'soft', 'rammer'], size, elevation,
  render: { palette, depth: 30, anchor: 'center', shadow: true, facing: 'diagonal' },
  modules: [
    { use: 'ai.diver', config: { launch: 'immediate', entryTicks: 1, entryArc: 0, speed: dive.speed, speedScalar: 'dive.speed', weaveAmp: dive.weaveAmp,
      weaveRate: dive.weaveRate, exit: 'despawn', holdFireScalar: 'hold.fire', armMargin: 1.5 } },
    { use: 'weapon.gun', config: { projectile: 'flak-shell', speed: 7, cooldownTicks: 4, aim: 'target', aimTag: 'ship', trigger: 'auto',
      autoEveryTicks: gun.everyTicks, maxAlive: gun.maxAlive, muzzleY: size.h / 2, armTag: 'armed', everyScalar: 'fire.every' } },
    { use: 'rule.destructible', config: { hp, score, hitTicks: hp > 1 ? 6 : 0 } },
  ],
});

const shot = (role: string, tag: string, palette: number, size: number, lifeTicks: number): GameSpecV1['archetypes'][string] => ({
  role, tags: [tag], size: { w: size, h: size }, elevation: ALTITUDE,
  render: { palette, depth: 40, anchor: 'center', facing: 'symmetric' },
  modules: [{ use: 'body.ballistic', config: { gravity: 0, lifeTicks, landOnSurfaces: false, leaveTag: tag } }],
});

const globals: ModuleUseV1[] = [
  { use: 'world.scrollTrack', config: track },
  { use: 'rule.contactDamage', key: 'guns', config: { attackerTag: 'player-fire', victimTag: 'soft', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: true } },
  { use: 'rule.contactDamage', key: 'flak', config: { attackerTag: 'enemy-fire', victimTag: 'ship', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: true } },
  { use: 'rule.contactDamage', key: 'rams', config: { attackerTag: 'rammer', victimTag: 'ship', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: false } },
  { use: 'rule.contactDamage', key: 'obstacles', config: { attackerTag: 'obstacle', victimTag: 'ship', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: false } },
  { use: 'rule.targets', config: { targetTag: 'primary', count: 5, extractOn: 'route-end', progressScalar: 'route.progress', bonus: 2000 } },
  // The bomb bay's stock: twelve; each one unused pays at extraction (after
  // the checklist, which signals it).
  { use: 'rule.resource', key: 'bombs', config: { label: 'BOMBS', display: 'units', capacity: 12, drainPerTick: 0, startRunning: true, fillOn: [],
    adjust: [{ signal: 'released', fromTag: 'player', amount: -1 }], cashOutOn: ['extracted'], cashOutPoints: 250, onEmpty: 'none', scalar: 'bombs.level' } },
  // Sector escalation: fighters dive faster and every gun fires more often.
  { use: 'flow.escalation', config: { curves: [
    { scalar: 'dive.speed', index: 'sector.number', kind: 'geometric', base: 1, rate: 1.15, min: 1, max: 2 },
    { scalar: 'fire.every', index: 'sector.number', kind: 'linear', base: 26, rate: -4, min: 14, max: 26 },
  ] } },
  { use: 'flow.objective', config: { win: 'goal', count: 0, milestone: 0, lose: 'players-out', requiredTag: 'primary', collectTag: 'primary', celebrate: false } },
];

export const BLUE_MAX_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Blue Max',
  summary: 'a pseudo-isometric scrolling flight-strike arcade game over enemy countryside',
  world: { w: W, h: H, orientation: 'portrait' },
  camera: { mode: 'auto-scroll' },
  players: { archetypes: ['strike-plane'], spawns: [{ x: ANCHOR.x, y: ANCHOR.y + PLANE / 2, facing: 1 }] },
  archetypes: {
    'strike-plane': {
      role: 'the player strike aircraft: a single-seat propeller plane with belly bomb racks',
      tags: ['player', 'ship'], size: { w: PLANE, h: PLANE }, elevation: ALTITUDE,
      render: { palette: 1, depth: 50, anchor: 'center', shadow: true, facing: 'diagonal' },
      modules: [
        { use: 'body.flight', config: { speed: 5.5, accel: 60, band: { left: 0.3, right: 0.3, top: 3, bottom: 1.8 }, faceMotion: false, recoverInPlace: true } },
        { use: 'weapon.gun', config: { projectile: 'player-shot', speed: 16, cooldownTicks: 5, aim: 'heading', headingDeg: HEADING, trigger: 'a',
          autoEveryTicks: 1, maxAlive: 6, muzzleY: PLANE / 2 } },
        { use: 'weapon.bomb', config: { projectile: 'iron-bomb', reticle: 'impact-ring', trigger: 'b', cooldownTicks: 12, fallTicks: 36,
          ammoScalar: 'bombs.level', targetTag: 'primary' } },
        // Five hull points that recover in place (no respawn delay).
        { use: 'core.vitals', config: { lives: 5, hurtTicks: 8, respawnTicks: 0, invulnTicks: 45 } },
        { use: 'fx.condition', config: { stages: [{ atOrBelow: 3, everyTicks: 12, effect: 'smoke', speedMul: 1 },
          { atOrBelow: 1, everyTicks: 6, effect: 'blaze', speedMul: 0.7 }] } },
      ],
    },
    'enemy-fighter': aircraft('a fast enemy interceptor with swept wings and a hostile chevron', 3,
      { w: 1, h: 1 }, ALTITUDE, { speed: 4.2, weaveAmp: 2, weaveRate: 2.2 }, { everyTicks: 26, maxAlive: 2 }, 1, 150),
    'enemy-bomber': aircraft('a slow twin-engine enemy bomber with a tail-gun blister', 4,
      { w: 1.9, h: 1.4 }, ALTITUDE + 0.3, { speed: 1.3, weaveAmp: 0.5, weaveRate: 0.8 }, { everyTicks: 34, maxAlive: 3 }, 3, 400),
    'flak-turret': {
      role: 'a sandbagged anti-aircraft gun emplacement with a long twin barrel',
      tags: ['enemy', 'ground', 'soft'], size: { w: 1, h: 1 },
      render: { palette: 5, depth: 10, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.static', config: {} },
        { use: 'ai.turret', config: { targetTag: 'ship', range: 8.5, arcCenter: 180, arcHalf: 80, inset: 0.3, warnTicks: 24, burstTicks: 30, restTicks: 50, armTag: 'armed' } },
        { use: 'weapon.gun', config: { projectile: 'flak-shell', speed: 6, cooldownTicks: 4, aim: 'target', aimTag: 'ship', trigger: 'auto',
          autoEveryTicks: 12, maxAlive: 3, muzzleY: 0.5, armTag: 'armed' } },
        { use: 'rule.destructible', config: { hp: 1, score: 200, hitTicks: 0 } },
      ],
    },
    'supply-truck': {
      role: 'a canvas-backed enemy supply truck',
      tags: ['ground', 'soft'], size: { w: 0.8, h: 1 },
      render: { palette: 6, depth: 8, anchor: 'center' },
      modules: [
        { use: 'body.ballistic', config: { gravity: 0, lifeTicks: 0, landOnSurfaces: false, leaveTag: 'driving' } },
        { use: 'rule.destructible', config: { hp: 1, score: 100, hitTicks: 0 } },
      ],
    },
    'marked-target': {
      role: 'a hardened concrete enemy bunker ringed with a painted target marker',
      tags: ['ground', 'primary', 'obstacle'], size: { w: 1.8, h: 1.6 },
      render: { palette: 2, depth: 6, anchor: 'center', neutral: true, facing: 'symmetric' },
      modules: [
        { use: 'body.static', config: {} },
        { use: 'rule.destructible', config: { hp: 1, score: 1000, hitTicks: 0 } },
      ],
    },
    'river-bridge': {
      role: 'a steel truss bridge span',
      tags: ['ground', 'primary'], size: { w: 3.2, h: 1.4 },
      render: { palette: 2, depth: 5, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.static', config: {} },
        { use: 'rule.destructible', config: { hp: 1, score: 1500, hitTicks: 0 } },
      ],
    },
    'extraction-pad': {
      role: 'a friendly concrete landing strip with chevron stripes marking the extraction point',
      tags: ['ground'], size: { w: 4, h: 2.2 },
      render: { palette: 7, depth: 4, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.static', config: {} }],
    },
    'player-shot': shot('a short bright machine-gun tracer fired forward', 'player-fire', 7, 0.24, 24),
    'flak-shell': shot('a glowing flak tracer shell with a short smoke tail', 'enemy-fire', 6, 0.34, 0),
    'iron-bomb': {
      role: 'a small finned iron bomb',
      tags: ['bomb'], size: { w: 0.36, h: 0.36 }, elevation: ALTITUDE,
      render: { palette: 0, depth: 45, anchor: 'center', shadow: true, facing: 'symmetric' },
      modules: [
        { use: 'body.fall', config: {} },
        { use: 'rule.blast', config: { victimTag: 'ground', radius: 1.1, amount: 1 } },
      ],
    },
    'impact-ring': {
      role: 'a thin glowing targeting ring',
      tags: [], size: { w: 1, h: 1 },
      render: { palette: 5, depth: 3, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.reticle', config: {} }],
    },
  },
  globals,
  // The concept's HUD (hull, bombs, primaries, score, distance to
  // extraction), short enough for one portrait line.
  hud: [{ from: 'score', field: 'score' }, { from: 'core.vitals', field: 'lives', label: 'HULL' }, { from: 'bombs', field: 'level' },
    { from: 'rule.targets', field: 'targets', label: 'TGT' }, { from: 'world.scrollTrack', field: 'distance' }],
  input: { profile: 'move4-fire-action', labels: { a: 'FIRE', b: 'BOMB' } },
  presentation: {
    projection: 'top-down',
    backgroundRole: 'seamless top-down patchwork farmland with dirt roads, hedgerows, tree stands and a river bend under dawn haze',
    palette: ['#101018', '#e8e8d8', '#5f8f3f', '#2f4f8f', '#8a6a3a', '#f0d84c', '#c05a28', '#2f9450'],
    backgroundPaletteIndex: 2,
  },
};

export const BLUE_MAX_TEMPLATE: TemplateV1 = {
  id: 'strike-flight/blue-max',
  family: 'strike-flight',
  title: 'Blue Max strike flight',
  pitch: 'Diagonal-scrolling strike run: strafe planes and trucks, lead your bombs onto five marked targets, survive to extraction.',
  disclosure: {
    coreVerbs: ['steer', 'fire', 'bomb'],
    mechanics: [
      'The ground scrolls continuously on a shallow diagonal toward the upper right; you steer freely inside the band and can never turn back.',
      'Your guns fire forward along the scroll and destroy enemy fighters, bombers, supply trucks and flak crews, but pass over the hardened primary targets.',
      'Twelve bombs: a bomb keeps your momentum and falls for over a second, so the impact ring slides ahead of you — release early, when the ring sits on the target.',
      'Five marked primary targets lie far apart along the route — fuel depot, hangar, factory, radio mast and river bridge — and only bombs count them.',
      'Your aircraft has five hull points: flak, fighter fire, rams and clipping a hardened building each cost one; it smokes at three and trails fire, steering slower, at one.',
      'Three sectors escalate: open farmland with trucks, an industrial belt with paired flak, a river valley where heavy bombers cross; fighters dive faster and guns fire more often.',
      'Fairness: flak fires only on screen and ahead of you after a blinking swivel, fighters make visible diving passes, and the first eight seconds are a safe corridor.',
      'Unused bombs pay a bonus at extraction.',
    ],
    controls: 'Steer in four directions; fire shoots forward; bomb drops one bomb onto the impact ring.',
    winCondition: 'Destroy all five marked primary targets with bombs and reach the extraction pad; arriving with fewer, or being shot down, fails the mission.',
  },
  music: { mood: 'energetic focused dawn air raid', genre: 'arcade electronic chiptune', tempoBpm: 142,
    instrumentation: ['synth lead', 'pulse bass', 'punchy drums', 'noise snare'] },
  spec: BLUE_MAX_SPEC,
  tunables: [
    { path: '/archetypes/strike-plane/modules/body.flight/speed', min: 4, max: 7.5 },
    { path: '/archetypes/strike-plane/modules/core.vitals/lives', min: 3, max: 7, integer: true },
    { path: '/archetypes/strike-plane/modules/weapon.bomb/fallTicks', min: 24, max: 45, integer: true },
    { path: '/archetypes/strike-plane/modules/weapon.bomb/cooldownTicks', min: 8, max: 24, integer: true },
    { path: '/archetypes/enemy-fighter/modules/ai.diver/speed', min: 3, max: 6 },
    { path: '/archetypes/enemy-bomber/modules/rule.destructible/hp', min: 2, max: 5, integer: true },
    { path: '/archetypes/flak-turret/modules/ai.turret/warnTicks', min: 15, max: 40, integer: true },
    { path: '/archetypes/flak-turret/modules/ai.turret/range', min: 6, max: 11 },
    { path: '/archetypes/river-bridge/modules/rule.destructible/hp', min: 1, max: 3, integer: true },
    { path: '/globals/world.scrollTrack/speed', min: 1.5, max: 2.4 },
    { path: '/globals/world.scrollTrack/heading', min: 0, max: 30 },
    { path: '/globals/bombs/capacity', min: 8, max: 16, integer: true },
    { path: '/globals/flow.escalation/curves/0/rate', min: 1, max: 1.3 },
    { path: '/globals/flow.escalation/curves/1/base', min: 18, max: 34 },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
    { path: '/globals/world.scrollTrack/sectors/*/marks/*/label', maxLength: 24 },
  ],
  // Every verb and the checklist must happen on every proof seed; whether a
  // given seed's flak lives long enough to telegraph is play, not proof.
  requirements: { events: ['fire', 'release', 'blast', 'dive', 'target-down', 'extract',
    'destroy:marked-target', 'destroy:river-bridge', 'destroy:supply-truck'] },
  signatureModules: ['world.scrollTrack', 'weapon.bomb', 'rule.blast', 'rule.targets'],
  // The craft spawns mid-band on the route line: steering moves it, the gun
  // fires a tracer, the bay drops a bomb (twelve in stock from the opening).
  verbProofs: [
    { verb: 'steer', input: 'right', ms: 500, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'fire', input: 'a', ms: 300, expect: { kind: 'event', event: 'fire' } },
    { verb: 'bomb', input: 'b', ms: 300, expect: { kind: 'event', event: 'release' } },
  ],
};
